'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { SubCategoriesEntity } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';

import { A11y, Navigation } from 'swiper/modules';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { cn } from '@eliadmin/core/src/utils/style-utils';

export function CategoriesSlider({ categories }: { categories: SubCategoriesEntity[] }) {
  const { t } = useTranslations();
  return (
    <Swiper
      navigation={true}
      modules={[Navigation, A11y]}
      a11y={{ prevSlideMessage: t('text3525'), nextSlideMessage: t('text3526') }}
      className={cn('mySwiper w-full overflow-visible', 'ea-categories-slider__swiper')}
      slidesPerView={'auto'}
      spaceBetween={16}
      breakpoints={{
        1024: {
          spaceBetween: 32,
        },
        768: {
          spaceBetween: 24,
        },
        480: {
          spaceBetween: 20,
        },
      }}
    >
      <div
        className={cn(
          'custom-container',
          'pointer-events-none absolute left-1/2 top-0 z-10 hidden h-[300px] w-full -translate-x-1/2 lg:block',
          'ea-categories-slider__invisible-container',
        )}
      >
        <div className="relative h-full w-full">
          <div className="absolute right-full top-0 z-20 h-[300px] w-[2000px] bg-white" />
        </div>
      </div>

      {categories.map(category => (
        <SwiperSlide
          key={category.name}
          className={cn(
            'group relative overflow-hidden',
            '!h-[145px] !w-[230px] sm:!h-[160px] sm:!w-[275px] md:!h-[170px] md:!w-[300px] lg:!h-[200px] lg:!w-[340px] xl:!h-[230px] xl:!w-[370px]',
          )}
        >
          <Link
            prefetch={false}
            href={category.slug}
            className="relative flex h-full w-full items-center justify-center"
          >
            <h2
              className={cn(
                'font-heading uppercase',
                'relative z-10 text-center text-lg font-medium text-white md:text-xl xl:text-2xl 2xl:text-3xl',
              )}
            >
              {category.name}
            </h2>
            <div
              className={cn(
                'absolute inset-0 z-[1] h-full w-full bg-black/20',
                'transition-all group-hover:bg-black/35',
              )}
            />
            <Image
              src={category.image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(category?.image, category?.name)}
              fill
              style={{ objectFit: 'cover', objectPosition: 'center' }}
              sizes="(min-width: 1280px) 370px, (min-width: 1024px) 340px, (min-width: 768px) 300px, (min-width: 640px) 275px, 230px"
              loading="lazy"
              className={cn(
                'absolute inset-0 z-0 h-full w-full',
                'transition-all group-hover:scale-110',
              )}
            />
          </Link>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}
